/** Binds the chat reducer to the streaming API and the active conversation. */

import { useCallback, useEffect, useReducer, useRef } from "react";
import { api, streamChat, type StreamHandle } from "../api.ts";
import { chatReducer, initialChatState, type ChatState } from "./chatReducer.ts";
import type { ModelId, ReasoningEffort } from "../../shared/models.ts";
import type { DepthChoice } from "../../shared/depth.ts";

export interface SendSettings {
  readonly model: ModelId;
  readonly thinking: boolean;
  readonly reasoningEffort: ReasoningEffort;
  /** May be `auto`; the server resolves it from the planner's read of the question. */
  readonly depth: DepthChoice;
  readonly supervised: boolean;
}

/**
 * What to load. The nonce exists so "New question" can clear an already-null
 * selection — without it, clicking it twice would be a no-op.
 */
export interface LoadTarget {
  readonly id: string | null;
  readonly nonce: number;
}

export interface UseChatResult {
  readonly state: ChatState;
  readonly send: (content: string, settings: SendSettings) => void;
  readonly stop: () => void;
  readonly dismissError: () => void;
}

export function useChat(
  target: LoadTarget,
  onConversationCreated: (id: string) => void,
  onTitleChanged: (id: string, title: string) => void,
): UseChatResult {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);

  const handleRef = useRef<StreamHandle | null>(null);
  // `send` needs the current conversation without being re-created each token.
  const conversationRef = useRef<string | null>(null);
  conversationRef.current = state.conversationId;

  // Load the selected conversation, ignoring responses that lose the race to a
  // later selection.
  useEffect(() => {
    let cancelled = false;
    const { id } = target;

    if (!id) {
      dispatch({ type: "load", conversationId: null, messages: [] });
      return;
    }

    void api
      .getConversation(id)
      .then((conversation) => {
        if (cancelled) return;
        dispatch({
          type: "load",
          conversationId: conversation.id,
          messages: conversation.messages,
        });
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: "load", conversationId: id, messages: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [target.id, target.nonce]);

  useEffect(() => () => handleRef.current?.cancel(), []);

  const send = useCallback(
    (content: string, settings: SendSettings) => {
      const trimmed = content.trim();
      if (trimmed.length === 0 || handleRef.current !== null) return;

      const conversationId = conversationRef.current;
      dispatch({ type: "submit", content: trimmed });

      const handle = streamChat(
        {
          ...(conversationId ? { conversationId } : {}),
          content: trimmed,
          model: settings.model,
          thinking: settings.thinking,
          reasoningEffort: settings.reasoningEffort,
          depth: settings.depth,
          supervised: settings.supervised,
        },
        (event) => {
          dispatch({ type: "event", event });

          if (event.type === "start" && !conversationId) {
            onConversationCreated(event.conversationId);
          }
          if (event.type === "title") {
            const id = conversationRef.current;
            if (id) onTitleChanged(id, event.title);
          }
        },
      );

      handleRef.current = handle;
      void handle.done.finally(() => {
        handleRef.current = null;
        dispatch({ type: "settled" });
      });
    },
    [onConversationCreated, onTitleChanged],
  );

  const stop = useCallback(() => handleRef.current?.cancel(), []);
  const dismissError = useCallback(() => dispatch({ type: "dismissError" }), []);

  return { state, send, stop, dismissError };
}
